import { useState, useCallback, useEffect } from 'react';
import { runImpactEffortTool } from '../../tools/impactEffortMatrix';
import { ItemInput } from './components/ItemInput';
import { ScoringView } from './components/ScoringView';
import { ResultsView } from './components/ResultsView';
import type { MatrixItem } from './types';
import ToolWrapper, { type ToolHostProps, type ToolResultItem } from '../../components/prioritization/ToolWrapper';
import { useToolMode, type ToolItem } from '../../contexts/toolItemsHooks';
import { useLiveToolSession } from '../../components/prioritization/session/liveExperience/useLiveToolSession';
import { LiveToolScaffold } from '../../components/prioritization/session/liveExperience/LiveToolScaffold';
import { LiveScaleScore } from '../../components/prioritization/session/liveExperience/liveTools/LiveScaleScore';
import { MatrixQuadrantsLive } from '../../components/prioritization/session/liveExperience/liveTools/MatrixQuadrantsLive';
import { useAttributesStore } from '../../stores/prioritization/attributesStore';

type AppState = 'input' | 'scoring' | 'results';

// Map quadrants to priority ranks
const QUADRANT_RANKS: Record<string, number> = {
  'quick-wins': 1,
  'big-bets': 2,
  'fill-ins': 3,
  'avoid': 4,
};

// Props for task mode
interface ImpactEffortMatrixProps extends ToolHostProps {
  taskMode?: boolean;
  taskId?: string;
  initialItems?: Array<{ id: string; title: string; description?: string }>;
  onComplete?: (data: { rankings: Array<{ itemId: string; rank: number; score?: number; category?: string }> }) => void;
}

function ImpactEffortMatrixContent({ taskMode, taskId: _taskId, initialItems, onComplete, ...rest }: ImpactEffortMatrixProps) {
  const toolMode = useToolMode();
  const getAttributeByKey = useAttributesStore(state => state.getAttributeByKey);
  const { isLiveSession, shouldAutoStart, importSourceLabel: liveImportLabel, completeLabel } = useLiveToolSession();
  const isAppMode = toolMode === 'app' || taskMode;
  const importSourceLabel = taskMode ? 'task' : liveImportLabel;
  
  // Convert initial items from task mode to MatrixItem format
  const initialMatrixItems: MatrixItem[] = (taskMode && initialItems) 
    ? initialItems.map(item => ({
        id: item.id,
        name: item.title,
        impact: null,
        effort: null,
        quadrant: null,
      }))
    : [];
  
  // If in task mode with initial items, start in input state with items pre-loaded
  const [state, setState] = useState<AppState>('input');
  const [items, setItems] = useState<MatrixItem[]>(initialMatrixItems);
  const [currentItemIndex, setCurrentItemIndex] = useState(0);
  const [scoringPhase, setScoringPhase] = useState<'impact' | 'effort'>('impact');
  /** Live only: set when facilitator/participant taps Done on the final effort step. */
  const [liveScoringComplete, setLiveScoringComplete] = useState(false);
  
  // Track source IDs for imported items
  const [sourceIdMap, setSourceIdMap] = useState<Record<string, string>>({});

  // Quadrant assignment comes from the canonical engine adapter
  // (src/tools/impactEffortMatrix.ts). The page's 1–5 scale and midpoint of 3
  // are passed as tool config so classification is engine-owned.
  const assignQuadrants = useCallback(async (list: MatrixItem[]): Promise<MatrixItem[]> => {
    const complete = list.filter(i => i.impact != null && i.effort != null);
    if (complete.length === 0) return list;
    const scoreSet = await runImpactEffortTool(
      complete.map(i => ({
        id: i.id,
        label: i.name,
        attributes: { impact: i.impact, effort: i.effort },
      })),
      null,
      { scale_max: 5, midpoint_threshold: 3 }
    );
    const quadrantById = new Map<string, MatrixItem['quadrant']>(
      scoreSet.items.map(s => [s.item_id as string, s.quadrant as MatrixItem['quadrant']])
    );
    return list.map(i => ({ ...i, quadrant: quadrantById.get(i.id) ?? i.quadrant }));
  }, []);

  // Keep quadrants materialized in state: whenever a fully-scored item lacks
  // its quadrant (manual scoring in progress, live mode), batch-assign.
  useEffect(() => {
    if (!items.some(i => i.impact != null && i.effort != null && i.quadrant == null)) return;
    let cancelled = false;
    void assignQuadrants(items).then(next => {
      if (!cancelled) setItems(next);
    });
    return () => {
      cancelled = true;
    };
  }, [items, assignQuadrants]);

  // Handler for importing items from workspace or session.
  // When items carry pre-existing impact/effort attribute values (data-driven mode),
  // quadrants are computed immediately and the tool goes straight to the results view.
  // Items missing either value fall through to manual scoring.
  const handleImportItems = useCallback((toolItems: ToolItem[]) => {
    const newSourceIdMap: Record<string, string> = {};
    toolItems.forEach(item => {
      newSourceIdMap[item.id] = item.sourceId ?? item.id;
    });
    setSourceIdMap(prev => ({ ...prev, ...newSourceIdMap }));

    const newItems: MatrixItem[] = toolItems.map((item) => {
      const rawImpact = item.attributes?.impact;
      const rawEffort = item.attributes?.effort;
      const impact = typeof rawImpact === 'number' && rawImpact >= 1 && rawImpact <= 5 ? rawImpact : null;
      const effort = typeof rawEffort === 'number' && rawEffort >= 1 && rawEffort <= 5 ? rawEffort : null;
      return {
        id: item.id,
        name: item.name,
        impact,
        effort,
        quadrant: null, // assigned by the engine adapter (effect above)
      };
    });

    const allPrePopulated = newItems.every(i => i.impact !== null && i.effort !== null);

    setItems(prev => {
      const existing = prev.filter(p => !newItems.some(n => n.id === p.id));
      return [...existing, ...newItems];
    });

    if (allPrePopulated && newItems.length > 0 && !isLiveSession) {
      // All items have accepted values → skip manual scoring, jump to results
      setState('results');
    } else if (shouldAutoStart && newItems.length > 0) {
      setCurrentItemIndex(0);
      setScoringPhase('impact');
      setState('scoring');
    }
  }, [shouldAutoStart, isLiveSession]);
  
  // Get results for saving as session result
  const handleGetResults = useCallback((): ToolResultItem[] => {
    const sortedItems = items
      .filter(item => item.impact != null && item.effort != null && item.quadrant != null)
      .sort((a, b) => QUADRANT_RANKS[a.quadrant!] - QUADRANT_RANKS[b.quadrant!]);

    return sortedItems.map((item, idx) => ({
      id: sourceIdMap[item.id] || item.id,
      name: item.name,
      rank: idx + 1,
      category: item.quadrant || undefined,
      score: item.impact != null ? item.impact - (item.effort ?? 0) : undefined,
      scoreBreakdown: {
        impact: item.impact ?? 0,
        effort: item.effort ?? 0,
      },
      classificationBreakdown: item.quadrant
        ? { quadrant: item.quadrant }
        : undefined,
      metadata: {
        impact: item.impact,
        effort: item.effort,
        quadrant: item.quadrant,
      },
    }));
  }, [items, sourceIdMap]);

  const handleStart = (itemNames: string[]) => {
    const newItems: MatrixItem[] = itemNames.map((name, index) => ({
      id: `item-${index}`,
      name,
      impact: null,
      effort: null,
      quadrant: null,
    }));
    setItems(newItems);
    setCurrentItemIndex(0);
    setScoringPhase('impact');
    setState('scoring');
  };
  
  const handleScoreUpdate = (field: 'impact' | 'effort', value: number | null) => {
    const updatedItems = [...items];
    updatedItems[currentItemIndex] = {
      ...updatedItems[currentItemIndex],
      [field]: value,
    };
    setItems(updatedItems);
  };

  const handleNext = async () => {
    if (scoringPhase === 'impact') {
      setScoringPhase('effort');
    } else if (scoringPhase === 'effort') {
      // Move to next item or finish
      if (currentItemIndex < items.length - 1) {
        setCurrentItemIndex(currentItemIndex + 1);
        setScoringPhase('impact');
      } else {
        const calculatedItems = await assignQuadrants(items);
        setItems(calculatedItems);
        if (isLiveSession) {
          setLiveScoringComplete(true);
        } else {
          setState('results');
        }

        // If in task mode, call onComplete with the results
        if (taskMode && onComplete) {
          const rankings = calculatedItems
            .filter(item => item.quadrant !== null)
            .sort((a, b) => QUADRANT_RANKS[a.quadrant!] - QUADRANT_RANKS[b.quadrant!])
            .map((item, idx) => ({
              itemId: item.id,
              rank: idx + 1,
              score: item.impact ? (item.impact - (item.effort || 0)) : undefined,
              category: item.quadrant || undefined,
            }));
          onComplete({ rankings });
        }
      }
    }
  };

  const handleBack = () => {
    if (scoringPhase === 'effort') {
      setScoringPhase('impact');
    } else if (scoringPhase === 'impact' && currentItemIndex > 0) {
      setCurrentItemIndex(currentItemIndex - 1);
      setScoringPhase('effort');
    }
  };

  const handleReset = () => {
    setState('input');
    setItems([]);
    setCurrentItemIndex(0);
    setScoringPhase('impact');
    setLiveScoringComplete(false);
    setSourceIdMap({});
  };

  const canProceed = () => {
    const current = items[currentItemIndex];
    if (!current) return false;
    if (scoringPhase === 'impact') return current.impact !== null && current.impact >= 1 && current.impact <= 5;
    if (scoringPhase === 'effort') return current.effort !== null && current.effort >= 1 && current.effort <= 5;
    return false;
  };

  const currentItem = items[currentItemIndex];
  const progress = state === 'scoring' 
    ? ((currentItemIndex * 2 + (scoringPhase === 'impact' ? 0 : 1)) / (items.length * 2)) * 100
    : 100;

  const allScored =
    items.length > 0 && items.every(i => i.impact != null && i.effort != null);
  const canSubmitLive = isLiveSession && allScored;
  // Whole items, not half-steps: an item is placed once it has both ratings.
  const placedCount = items.filter(i => i.impact != null && i.effort != null).length;

  return (
    <ToolWrapper
      toolName="Impact-Effort Matrix"
      toolDescription="Prioritize items using a 2×2 matrix"
      onImportItems={handleImportItems}
      onGetResults={canSubmitLive || state === 'results' ? handleGetResults : undefined}
      itemCount={items.length}
      {...rest}
    >
      <div className="w-full">
        {/* Main Content */}
        <div>
          {state === 'input' && (
            <>
              {isAppMode && items.length === 0 && !taskMode && (
                <div className="mb-6 border border-[var(--slate)] bg-[var(--slate-dim)] p-4">
                  <p className="text-sm text-[var(--text-sec)]">
                    {`Import items from your ${importSourceLabel} using the button in the header to begin Impact/Effort scoring.`}
                  </p>
                </div>
              )}
              {/* Standalone mode - show ItemInput */}
              {!isAppMode && <ItemInput onStart={handleStart} />}
            </>
          )}

          {state === 'scoring' && currentItem && isLiveSession && (
            <LiveToolScaffold
              title="Impact / effort"
              subtitle={
                liveScoringComplete
                  ? 'Review your matrix, then submit your response below'
                  : 'Rate each item on impact and effort (1–5)'
              }
              progressDone={placedCount}
              progressTotal={items.length}
            >
              {liveScoringComplete ? (
                <div className="border border-alpine-green-500/35 bg-alpine-green-500/10 px-4 py-3 mb-6">
                  <p className="font-body text-sm text-alpine-green-400">
                    All items rated. Review the matrix below, then use{' '}
                    <span className="font-medium">{completeLabel}</span> at the bottom of the screen.
                  </p>
                </div>
              ) : (
                <LiveScaleScore
                  itemName={currentItem.name}
                  itemIndex={currentItemIndex}
                  totalItems={items.length}
                  phaseTitle={scoringPhase === 'impact' ? 'Impact' : 'Effort'}
                  valueDefinition={getAttributeByKey(scoringPhase)?.definition}
                  phaseDescription={
                    scoringPhase === 'impact'
                      ? 'How valuable is this? (5 = highest impact)'
                      : 'How costly is this? (5 = highest effort)'
                  }
                  value={scoringPhase === 'impact' ? currentItem.impact : currentItem.effort}
                  onValueChange={v => handleScoreUpdate(scoringPhase, v)}
                  onNext={handleNext}
                  onBack={
                    scoringPhase === 'effort' || currentItemIndex > 0 ? handleBack : undefined
                  }
                  isLastStep={
                    scoringPhase === 'effort' && currentItemIndex === items.length - 1
                  }
                />
              )}
              {(liveScoringComplete || allScored) && (
                <div className={liveScoringComplete ? '' : 'mt-8'}>
                  <MatrixQuadrantsLive
                    quadrantOrder={['quick-wins', 'big-bets', 'fill-ins', 'avoid']}
                    items={items
                      .filter(i => i.quadrant)
                      .map(i => ({ id: i.id, name: i.name, quadrant: i.quadrant! }))}
                  />
                </div>
              )}
            </LiveToolScaffold>
          )}

          {state === 'scoring' && currentItem && !isLiveSession && (
            <ScoringView
              item={currentItem}
              itemIndex={currentItemIndex}
              totalItems={items.length}
              phase={scoringPhase}
              onScoreUpdate={handleScoreUpdate}
              onNext={handleNext}
              onBack={handleBack}
              canProceed={canProceed()}
              progress={progress}
            />
          )}

          {state === 'results' && (
            <ResultsView 
              items={items} 
              onReset={handleReset}
            />
          )}
        </div>
      </div>
    </ToolWrapper>
  );
}

export default function ImpactEffortMatrix(props: ImpactEffortMatrixProps) {
  return <ImpactEffortMatrixContent {...props} />;
}
